import React, { useCallback } from 'react';
import {
  Task,
  Contact,
  CreateTaskStatus,
  UpdateTaskStatus,
  TaskStatus,
} from '@admiin-com/ds-graphql';
import { useFormContext } from 'react-hook-form';
import { useSnackbar } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { useCreateTask } from '../../../hooks/useCreateTask';
import { BillCreateFormData } from './TaskCreationProvider';
import { useModal } from '../../components/ModalManager/ModalContext';
import { useGetTaskInput } from './useGetTaskInput';
import { useUpdateTask } from '../../../hooks/useUpdateTask/useUpdateTask';

export type settlementStatus = 'Payable' | 'Refundable';
export type Signer = {
  id?: string;
  email: string;
  name: string;
  signerType: 'CONTACT' | 'ENTITY_USER' | 'GUEST';
  entityId?: string;
  secondName?: string;
  mobile?: string;
  createContact?: boolean;
  // No Need to send backend
  color?: any;
  noLookup?: boolean;
};
export function useTaskCreate({
  onSuccess,
  isTaxCreation,
  taskType,
}: {
  onSuccess?: (task: Task) => void;
  isTaxCreation?: boolean;
  taskType?: 'tax' | 'esignature' | 'invoice' | 'paybill';
}) {
  const { t } = useTranslation();

  const [createTask] = useCreateTask();
  const [updateTask] = useUpdateTask();

  const [verified, setVerified] = React.useState(false);

  const methods = useFormContext<BillCreateFormData>();

  const createTaskInputValues = useGetTaskInput();
  const createTaskWithDraft = async (
    isDraft = false,
    taskTypeForCreate?: 'tax' | 'esignature' | 'invoice' | 'paybill',
    status?: TaskStatus
  ) => {
    const values = methods.getValues();
    const task = await createTaskInputValues(values, {
      savingAsDraft: isDraft || status === TaskStatus.REVIEW,
      taskType: isTaxCreation ? 'tax' : taskTypeForCreate ?? taskType,
      status,
    });

    const id = values.id;
    const entityId = values.entityId;

    let createdTaskData;

    if (id && entityId) {
      createdTaskData = await updateTask({
        variables: {
          input: {
            id,
            ...task,
            entityId,
            status: status
              ? (status as any)
              : isDraft
              ? CreateTaskStatus.DRAFT
              : UpdateTaskStatus.INCOMPLETE,
          },
        },
      });
      const createdTask = createdTaskData?.data?.updateTask;
      return createdTask;
    } else {
      createdTaskData = await createTask({
        variables: {
          input: {
            ...task,
            ...(status ? { status: status as any } : {}),
          },
        },
      });

      const createdTask = createdTaskData?.data?.createTask;
      return createdTask;
    }
  };

  const createDocument = async (values: BillCreateFormData | null = null) => {
    if (values) {
      methods.reset(values);
    }
    return handleSubmit('esignature');
  };

  const saveDocument = async (
    values: BillCreateFormData | null = null,
    status: TaskStatus = TaskStatus.DRAFT
  ) => {
    if (values) {
      methods.reset(values);
    }
    return handleSave('esignature', status);
  };

  const { openModal } = useModal();
  const showSnackbar = useSnackbar();

  const handleSubmit = useCallback(
    async (taskType?: 'tax' | 'esignature' | 'invoice' | 'paybill') => {
      try {
        const createdTask = await createTaskWithDraft(false, taskType);
        onSuccess?.(createdTask);
        return createdTask;
      } catch (err: any) {
        const updatingContact =
          methods.watch('futureTaskInput.from') ?? methods.watch('from');
        console.log('error creating draft task: ', err);
        switch (err.message) {
          case 'TO_MISSING_OWNER': {
            const toValue = methods.watch().to;
            // to can be either an Entity (with id) or a Contact (with entityId)
            const entityForOwner = {
              ...toValue,
              id: toValue.id || toValue.entityId,
            };
            openModal('addClientOwner', {
              entity: entityForOwner,
              onSuccess: async () => {
                // Apollo cache is already updated due to awaitRefetchQueries
                await handleSubmit();
              },
            });

            showSnackbar({
              message: t('clientMissingOwner', {
                ns: 'contacts',
                defaultValue:
                  'Client account needs to be created before sending tasks',
              }),
              severity: 'warning',
              horizontal: 'right',
              vertical: 'bottom',
            });
            break;
          }
          case 'TO_MISSING_EMAIL': //Contact doesn't have an email address
            openModal('contactDetail', {
              updatingContact: methods.watch().to,
              entityId: methods.watch().to.entityId,
              onSuccess: (updateContact: Contact) => {
                methods.setValue('to', updateContact);
                setTimeout(() => {
                  handleSubmit();
                }, 1000);
              },
            });

            showSnackbar({
              message: t(`Contact doesn't have an email address`, {
                ns: 'contacts',
              }),
              severity: 'warning',
            });
            break;
          case 'ENTITY_FROM_MISSING_DISBURSEMENT_METHOD':
            openModal('addPaymentMethod', {
              type: 'ReceivingAccount',
              onSuccess: () => {
                setTimeout(() => {
                  handleSubmit();
                }, 1000);
              },
            });
            break;
          case 'CONTACT_FROM_MISSING_DISBURSEMENT_METHOD':
            openModal('contactCreate', {
              entityId: updatingContact?.entityId,
              selected: updatingContact,
              onSuccess: () => {
                setTimeout(() => {
                  handleSubmit();
                }, 1000);
              },
            });
            showSnackbar({
              message: t('contactFromMissingDisbursementMethod', {
                ns: 'contacts',
              }),
              severity: 'warning',
              horizontal: 'right',
              vertical: 'bottom',
            });
            break;
          case 'ENTITY_FROM_NOT_VERIFIED':
            openModal('entityVerification', {
              entity: methods.watch().from,
              onSuccess: () => {
                setVerified(true);
              },
            });
            break;
          default:
            showSnackbar({
              message: t(err?.message, { ns: 'errors' }),
              severity: 'error',
              horizontal: 'right',
              vertical: 'bottom',
            });
            throw err;
        }
      }
    },

    [verified]
  );
  const handleSave = async (
    taskType?: 'tax' | 'esignature' | 'invoice' | 'paybill',
    status: TaskStatus = TaskStatus.DRAFT
  ) => {
    // Hanlde create task with status of draft here and do not validate in this function
    if (!(await methods.trigger('from')) || !(await methods.trigger('to')))
      return;
    try {
      const createdTask = await createTaskWithDraft(true, taskType, status);
      onSuccess?.(createdTask);
      return createdTask;
    } catch (err: any) {
      console.log('error updating entity: ', err);
      // setCreateError(err);
      showSnackbar({
        message: t(err?.message, { ns: 'errors' }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
      throw err;
    }
  };

  return {
    handleSubmit,
    handleSave,
    createDocument,
    saveDocument,
  };
}
